/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import * as React from 'react';
import {
    Button,
    getPayloadObject,
    getTelemetryAttributes,
    getTelemetryObject,
    INodeProps,
    ITelemetryContent,
    onTelemetryClick,
    Popover,
    TelemetryConstant
} from '@msdyn365-commerce-modules/utilities';
import { IAccountBlockData } from './account-block.data';
import { IAccountBlockProps, IMyAccountLinksData } from './account-block.props.autogenerated';
import { ILinksData, Link, ContentEditableEvent } from '@msdyn365-commerce/core';

export interface IAccountBlockViewProps extends IAccountBlockProps<IAccountBlockData> {
    AccountInfoDropdownParentContainer?: INodeProps;
    AccountInfoDropdownPopoverConentContainer?: INodeProps;
    accountInfoDropdownButton?: React.ReactNode;

    signOutLink?: React.ReactNode;
    signInLink?: React.ReactNode;
    accountLinks?: React.ReactNode[];
}

/**
 *
 * AccountBlock component
 * @extends {React.PureComponent<IAccountBlockProps<IAccountBlockData>>}
 */
const AccountBlock = (props: IAccountBlockProps<IAccountBlockData>) => {
    const {
        data: { accountInformation }
    } = props;

    const customer = accountInformation && accountInformation.result;

    const popOverRef = React.useRef<HTMLButtonElement>(null);
    const [signinPopoverOpen, setSigninPopoverOpen] = React.useState(false);

    const displayName = customer ? customer.FirstName || customer.Name : undefined;

    const telemetryContent = React.useMemo(() => {
        return getTelemetryObject(props.context.request.telemetryPageName!, props.friendlyName, props.telemetry);
    }, [props]);

    const togglePopover = React.useCallback(() => {
        setSigninPopoverOpen(!signinPopoverOpen);
    }, [signinPopoverOpen]);

    const getAccountInfoDropdownButton = () => {
        const payLoad = getPayloadObject('click', telemetryContent, TelemetryConstant.MyProfile);
        const attributes = getTelemetryAttributes(telemetryContent, payLoad);
        return (
            displayName && (
                <Button
                    innerRef={popOverRef}
                    className={'ms-header__profile-button'}
                    aria-describedby='myprofilePopover'
                    onClick={togglePopover}
                    color='link'
                    aria-expanded={signinPopoverOpen}
                    {...attributes}
                >
                    <span className='ms-profile-button-text'>{displayName}</span>
                </Button>
            )
        );
    };

    const viewProps: IAccountBlockViewProps = {
        ...props,
        AccountInfoDropdownParentContainer: {
            className: 'ms-header__account-info'
        },
        AccountInfoDropdownPopoverConentContainer:
            (displayName && {
                tag: Popover,
                id: 'myprofilePopover',
                className: 'ms-header__account-info-content',
                placement: 'bottom',
                isOpen: signinPopoverOpen,
                target: popOverRef,
                toggle: togglePopover
            }) ||
            undefined,
        accountInfoDropdownButton: getAccountInfoDropdownButton(),
        signInLink: getSigninButton(props, telemetryContent),
        signOutLink: getSignOutButton(props, telemetryContent),
        accountLinks: getAccountLinks(props, telemetryContent)
    };

    return props.renderView(viewProps);
};

const getSigninButton = (props: IAccountBlockProps<IAccountBlockData>, telemetryContent: ITelemetryContent) => {
    const {
        data: { accountInformation },
        context: {
            request: { user }
        },
        resources
    } = props;

    const customer = accountInformation && accountInformation.result;
    const payLoad = getPayloadObject('click', telemetryContent, TelemetryConstant.SignIn);
    const attributes = getTelemetryAttributes(telemetryContent, payLoad);
    return (
        !customer && (
            <Button
                className='ms-header__signin-button'
                title={resources.signInLinkText}
                href={user && user.signInUrl}
                aria-label={resources.signInLinkAriaText}
                onClick={onTelemetryClick(telemetryContent, payLoad, 'Sign in')}
                {...attributes}
            >
                <span className='ms-header__signin-button-text' aria-hidden={true}>
                    {resources.signInLinkText}
                </span>
            </Button>
        )
    );
};

const getSignOutButton = (props: IAccountBlockProps<IAccountBlockData>, telemetryContent: ITelemetryContent) => {
    const {
        data: { accountInformation },
        context: {
            request: { user }
        },
        resources
    } = props;

    const customer = accountInformation && accountInformation.result;
    const displayName = customer ? customer.FirstName || customer.Name : undefined;

    const payLoad = getPayloadObject('click', telemetryContent, TelemetryConstant.SignOut);
    const attributes = getTelemetryAttributes(telemetryContent, payLoad);
    return (
        customer &&
        displayName && (
            <Button
                className={'ms-header__signout-button'}
                title={resources.signOutLinkText}
                href={(user && user.signOutUrl) || ''}
                aria-label={resources.signOutLinkAriaText}
                onClick={onTelemetryClick(telemetryContent, payLoad, 'Sign out')}
                {...attributes}
            >
                <span className='ms-header__signout-button-text' aria-hidden={true}>
                    {resources.signOutLinkText}
                </span>
            </Button>
        )
    );
};

const getAccountLinks = (props: IAccountBlockProps<IAccountBlockData>, telemetryContent: ITelemetryContent) => {
    const {
        data: { accountInformation },
        context,
        config: { myAccountLinks }
    } = props;

    const customer = accountInformation && accountInformation.result;
    const displayName = customer ? customer.FirstName || customer.Name : undefined;
    const payLoad = getPayloadObject('click', telemetryContent, '');
    if (!customer || !displayName || !myAccountLinks || !myAccountLinks.length) {
        return undefined;
    }
    return myAccountLinks.map((cta: IMyAccountLinksData, index: number) => {
        payLoad.contentAction.etext = cta.linkText;
        const attributes = getTelemetryAttributes(telemetryContent, payLoad);
        const editableLink: ILinksData = {
            ariaLabel: cta.ariaLabel,
            className: 'ms-signin-info__account-link-button',
            linkText: cta.linkText,
            linkUrl: cta.linkUrl.destinationUrl,
            openInNewTab: cta.openInNewTab,
            role: 'button',
            additionalProperties: attributes
        };

        return (
            <Link
                key={index}
                link={editableLink}
                editProps={{ onTextChange: handleLinkTextChange(props, index), requestContext: context.request }}
            />
        );
    });
};

const handleLinkTextChange = (props: IAccountBlockProps<IAccountBlockData>, linkIndex: number) => (event: ContentEditableEvent) => {
    if (props.config.myAccountLinks && props.config.myAccountLinks[linkIndex]) {
        props.config.myAccountLinks[linkIndex].linkText = event.target.value;
    }
};

export default AccountBlock;
